import React, { useState } from 'react';
import { CheckCircle, Plus, Phone, Globe, Edit2, X, Trash2 } from 'lucide-react';
import { useApp } from '../context/AppContext';
import { Card, PageHeader, ProgressBar, Badge } from '../components/Card';

const CATEGORIES = ['All Tasks', 'Utilities', 'Services', 'Admin', 'Home Prep', 'Moving'];

const emptyTask = {
  title: '', category: 'Utilities', priority: 'medium',
  provider: '', phone: '', website: '', cost: '', note: '',
};

export default function MoveIn() {
  const [activeCategory, setActiveCategory] = useState('All Tasks');
  const [showModal, setShowModal] = useState(false);
  const [editingTask, setEditingTask] = useState(null);
  const [form, setForm] = useState({ ...emptyTask });
  const { tasks, toggleTask, getTaskStats, addTask, updateTask, deleteTask, showToast } = useApp();
  const stats = getTaskStats();

  const filteredTasks = activeCategory === 'All Tasks'
    ? tasks
    : tasks.filter(t => t.category === activeCategory);

  const categoryCounts = CATEGORIES.map(cat => ({
    name: cat,
    count: cat === 'All Tasks' ? tasks.length : tasks.filter(t => t.category === cat).length,
  }));

  const openAdd = () => {
    setEditingTask(null);
    setForm({ ...emptyTask });
    setShowModal(true);
  };

  const openEdit = (task) => {
    setEditingTask(task.id);
    setForm({
      title: task.title || '',
      category: task.category || 'Utilities',
      priority: task.priority || 'medium',
      provider: task.provider || '',
      phone: task.phone || '',
      website: task.website || '',
      cost: task.cost || '',
      note: task.note || '',
    });
    setShowModal(true);
  };

  const handleSave = () => {
    if (!form.title.trim()) return;
    if (editingTask) {
      updateTask(editingTask, { ...form });
      showToast('Task updated!');
    } else {
      addTask({ ...form });
      showToast('Task added!');
    }
    setShowModal(false);
    setForm({ ...emptyTask });
    setEditingTask(null);
  };

  const handleDelete = (taskId) => {
    if (window.confirm('Are you sure you want to delete this task? This cannot be undone.')) {
      deleteTask(taskId);
      showToast('Task deleted');
    }
  };

  const updateForm = (key, value) => setForm(prev => ({ ...prev, [key]: value }));

  return (
    <div>
      <PageHeader
        icon={<CheckCircle size={22} />}
        title="Move-In Checklist"
        subtitle="Track all the tasks you need to complete before and after moving into your new home."
      />

      <Card style={{ marginBottom: 20 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
          <h3 style={{ fontSize: 15, fontWeight: 600 }}>Overall Progress</h3>
          <span style={{ fontSize: 14, color: 'var(--gray-600)' }}>
            {stats.completed} of {stats.total} completed
          </span>
        </div>
        <ProgressBar value={stats.completed} max={stats.total} color="var(--green)" height={10} />
        <div style={{ fontSize: 13, color: 'var(--gray-500)', marginTop: 6 }}>
          {stats.total > 0 ? Math.round((stats.completed / stats.total) * 100) : 0}% complete
        </div>
      </Card>

      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginBottom: 16 }}>
        {categoryCounts.map(cat => (
          <button
            key={cat.name}
            onClick={() => setActiveCategory(cat.name)}
            style={{
              padding: '8px 16px', borderRadius: 20, fontSize: 13, fontWeight: 500,
              background: activeCategory === cat.name ? 'var(--dark)' : 'white',
              color: activeCategory === cat.name ? 'white' : 'var(--gray-700)',
              border: `1px solid ${activeCategory === cat.name ? 'var(--dark)' : 'var(--gray-300)'}`,
            }}
          >
            {cat.name} ({cat.count})
          </button>
        ))}
      </div>

      <button onClick={openAdd} style={{
        display: 'flex', alignItems: 'center', gap: 8, marginBottom: 16,
        padding: '10px 18px', background: 'white', border: '1px solid var(--primary)',
        borderRadius: 'var(--radius-sm)', color: 'var(--primary)', fontWeight: 500, fontSize: 14,
        cursor: 'pointer',
      }}>
        <Plus size={16} /> Add Task
      </button>

      {filteredTasks.map(task => (
        <Card key={task.id} style={{ marginBottom: 12, padding: 0, overflow: 'hidden' }}>
          <div style={{ padding: '16px 20px' }}>
            <div style={{ display: 'flex', alignItems: 'flex-start', gap: 12 }}>
              <button
                onClick={() => toggleTask(task.id)}
                style={{
                  width: 24, height: 24, borderRadius: '50%', flexShrink: 0,
                  border: task.status === 'Completed' ? 'none' : '2px solid var(--gray-300)',
                  background: task.status === 'Completed' ? 'var(--green)' : 'white',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  marginTop: 2, cursor: 'pointer',
                }}
              >
                {task.status === 'Completed' && <CheckCircle size={16} color="white" />}
              </button>
              <div style={{ flex: 1 }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 8 }}>
                  <h4 style={{
                    fontSize: 15, fontWeight: 600,
                    textDecoration: task.status === 'Completed' ? 'line-through' : 'none',
                    opacity: task.status === 'Completed' ? 0.6 : 1,
                  }}>{task.title}</h4>
                  <div style={{ display: 'flex', gap: 6, flexShrink: 0 }}>
                    <Badge color={
                      task.priority === 'high' ? 'var(--red)' :
                      task.priority === 'medium' ? 'var(--yellow)' : 'var(--gray-500)'
                    }>{task.priority}</Badge>
                    <Badge color={task.status === 'Completed' ? 'var(--green)' : 'var(--gray-400)'}>
                      {task.status}
                    </Badge>
                  </div>
                </div>
                {task.provider && (
                  <div style={{ fontSize: 13, color: 'var(--gray-500)', marginTop: 2 }}>{task.provider}</div>
                )}
                <div style={{ display: 'flex', gap: 16, marginTop: 8, flexWrap: 'wrap' }}>
                  {task.phone && (
                    <a href={`tel:${task.phone}`} style={{ display: 'flex', alignItems: 'center', gap: 4, fontSize: 13, color: 'var(--gray-600)', textDecoration: 'none' }}>
                      <Phone size={13} /> {task.phone}
                    </a>
                  )}
                  {task.website && (
                    <a href={task.website} target="_blank" rel="noopener noreferrer" style={{ display: 'flex', alignItems: 'center', gap: 4, fontSize: 13, color: 'var(--blue)', textDecoration: 'none' }}>
                      <Globe size={13} /> Website
                    </a>
                  )}
                  {task.cost && (
                    <span style={{ display: 'flex', alignItems: 'center', gap: 4, fontSize: 13, color: 'var(--gray-600)' }}>
                      $ {task.cost}
                    </span>
                  )}
                </div>
              </div>
            </div>
            {task.note && (
              <div style={{
                marginTop: 12, padding: '10px 14px', background: 'var(--gray-50)',
                borderRadius: 'var(--radius-sm)', fontSize: 13, color: 'var(--gray-600)',
                marginLeft: 36,
              }}>
                {task.note}
              </div>
            )}
            <div style={{ marginLeft: 36, marginTop: 8, display: 'flex', gap: 12 }}>
              <button onClick={() => openEdit(task)} style={{
                background: 'none', fontSize: 13, color: 'var(--gray-500)', fontWeight: 500, padding: 0, cursor: 'pointer',
              }}>Edit</button>
              <button onClick={() => handleDelete(task.id)} style={{
                background: 'none', fontSize: 13, color: 'var(--red)', fontWeight: 500, padding: 0, cursor: 'pointer',
              }}>Delete</button>
            </div>
          </div>
        </Card>
      ))}

      {showModal && (
        <div style={{
          position: 'fixed', inset: 0, zIndex: 300,
          background: 'rgba(0,0,0,0.5)',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          padding: 24,
        }} onClick={() => setShowModal(false)}>
          <div style={{
            background: 'white', borderRadius: 12, width: '100%', maxWidth: 500,
            padding: 24, position: 'relative',
          }} onClick={e => e.stopPropagation()}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
              <h3 style={{ fontSize: 18, fontWeight: 600 }}>{editingTask ? 'Edit Task' : 'Add New Task'}</h3>
              <button onClick={() => setShowModal(false)} style={{ background: 'none', color: 'var(--gray-500)', padding: 4, cursor: 'pointer' }}>
                <X size={20} />
              </button>
            </div>

            <div style={{ marginBottom: 12 }}>
              <label style={{ fontSize: 13, fontWeight: 500, display: 'block', marginBottom: 4 }}>Task Name *</label>
              <input value={form.title} onChange={e => updateForm('title', e.target.value)}
                placeholder="e.g. Set up internet service"
                style={{ width: '100%', padding: '10px 14px', border: '1px solid var(--gray-300)', borderRadius: 'var(--radius-sm)', fontSize: 14 }} />
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 12 }}>
              <div>
                <label style={{ fontSize: 13, fontWeight: 500, display: 'block', marginBottom: 4 }}>Category</label>
                <select value={form.category} onChange={e => updateForm('category', e.target.value)}
                  style={{ width: '100%', padding: '10px 14px', border: '1px solid var(--gray-300)', borderRadius: 'var(--radius-sm)', fontSize: 14, background: 'white' }}>
                  {CATEGORIES.filter(c => c !== 'All Tasks').map(c => <option key={c} value={c}>{c}</option>)}
                </select>
              </div>
              <div>
                <label style={{ fontSize: 13, fontWeight: 500, display: 'block', marginBottom: 4 }}>Priority</label>
                <select value={form.priority} onChange={e => updateForm('priority', e.target.value)}
                  style={{ width: '100%', padding: '10px 14px', border: '1px solid var(--gray-300)', borderRadius: 'var(--radius-sm)', fontSize: 14, background: 'white' }}>
                  <option value="low">Low</option>
                  <option value="medium">Medium</option>
                  <option value="high">High</option>
                </select>
              </div>
            </div>

            <div style={{ marginBottom: 12 }}>
              <label style={{ fontSize: 13, fontWeight: 500, display: 'block', marginBottom: 4 }}>Provider / Company</label>
              <input value={form.provider} onChange={e => updateForm('provider', e.target.value)}
                placeholder="e.g. Bell Canada"
                style={{ width: '100%', padding: '10px 14px', border: '1px solid var(--gray-300)', borderRadius: 'var(--radius-sm)', fontSize: 14 }} />
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 12 }}>
              <div>
                <label style={{ fontSize: 13, fontWeight: 500, display: 'block', marginBottom: 4 }}>Phone</label>
                <input value={form.phone} onChange={e => updateForm('phone', e.target.value)}
                  placeholder="613-555-1234"
                  style={{ width: '100%', padding: '10px 14px', border: '1px solid var(--gray-300)', borderRadius: 'var(--radius-sm)', fontSize: 14 }} />
              </div>
              <div>
                <label style={{ fontSize: 13, fontWeight: 500, display: 'block', marginBottom: 4 }}>Est. Cost</label>
                <input value={form.cost} onChange={e => updateForm('cost', e.target.value)}
                  placeholder="$50/month"
                  style={{ width: '100%', padding: '10px 14px', border: '1px solid var(--gray-300)', borderRadius: 'var(--radius-sm)', fontSize: 14 }} />
              </div>
            </div>

            <div style={{ marginBottom: 12 }}>
              <label style={{ fontSize: 13, fontWeight: 500, display: 'block', marginBottom: 4 }}>Website</label>
              <input value={form.website} onChange={e => updateForm('website', e.target.value)}
                placeholder="https://example.com"
                style={{ width: '100%', padding: '10px 14px', border: '1px solid var(--gray-300)', borderRadius: 'var(--radius-sm)', fontSize: 14 }} />
            </div>

            <div style={{ marginBottom: 20 }}>
              <label style={{ fontSize: 13, fontWeight: 500, display: 'block', marginBottom: 4 }}>Notes</label>
              <textarea value={form.note} onChange={e => updateForm('note', e.target.value)}
                placeholder="Any additional details..."
                rows={3}
                style={{ width: '100%', padding: '10px 14px', border: '1px solid var(--gray-300)', borderRadius: 'var(--radius-sm)', fontSize: 14, resize: 'vertical' }} />
            </div>

            <div style={{ display: 'flex', gap: 12, justifyContent: 'flex-end' }}>
              <button onClick={() => setShowModal(false)} style={{
                padding: '10px 20px', background: 'var(--gray-200)',
                borderRadius: 'var(--radius-sm)', fontSize: 14, cursor: 'pointer',
              }}>Cancel</button>
              <button onClick={handleSave} disabled={!form.title.trim()} style={{
                padding: '10px 20px', background: form.title.trim() ? 'var(--primary)' : 'var(--gray-300)',
                color: 'white', borderRadius: 'var(--radius-sm)', fontWeight: 600, fontSize: 14, cursor: form.title.trim() ? 'pointer' : 'default',
              }}>{editingTask ? 'Save Changes' : 'Add Task'}</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
